import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { useContext, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'

// Context
import { UserContext } from '../../context/UserContext'
import { NavigationContext } from '../../context/NavigationContext'

// Internal components
import AccountBadge from '../AccountBadge/AccountBadge'
import Button from '../Button/Button'
import Svg from '../Svg/Svg'

const AccountMenu = () => {
  const theme = process.env.NEXT_PUBLIC_THEME
  // Context
  const userContext = useContext(UserContext)
  const navigationContext = useContext(NavigationContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // State classes
  const openClass = 'is-open'
  const enabledClass = 'is-enabled'

  // States
  const [toggleState, setToggleState] = useState(false)
  const [scrollLockedState, setScrollLockedState] = useState(false)

  // Refs
  const containerRef = useRef()

  // ScrollLock on toggle
  useEffect(() => {
    if (toggleState) {
      disableBodyScroll(containerRef.current)
      setScrollLockedState(toggleState)
    }

    if (!toggleState && scrollLockedState) {
      enableBodyScroll(containerRef.current)
      setScrollLockedState(toggleState)
    }
  }, [toggleState])

  useEffect(() => {
    if (navigationContext.routeChange === true) {
      setToggleState(false)
      clearAllBodyScrollLocks()
    }
  }, [navigationContext.routeChange])

  return (
    <div
      className={`AccountMenu${toggleState ? ` ${openClass}` : ''}${
        userContext.loadedSession ? ` ${enabledClass}` : ''
      }`}
    >
      <div className="AccountMenu-inner">
        <div className="AccountMenu-container" ref={containerRef}>
          <div className="AccountMenu-toggle" onClick={() => setToggleState(!toggleState)}>
            {userContext.loggedIn ? (
              <AccountBadge initials={userContext.userInitials} />
            ) : (
              <AccountBadge icon="icon-profile" />
            )}
            <span className="AccountMenu-toggleLabel">{t('ACCOUNTMENU__TOGGLE_LABEL')}</span>
            <Svg
              modifiers={['AccountMenu-toggleIcon']}
              id={`${toggleState ? 'icon-chevron-up' : 'icon-chevron-down'}`}
            />
          </div>
          <div className="AccountMenu-flyout">
            {userContext.loggedIn ? (
              <>
                {process.env.NEXT_PUBLIC_LOGIN_TYPE === 'craft' ? (
                  <div className="AccountMenu-section">
                    <div className="AccountMenu-heading">
                      {userContext.userSession && (
                        <div className="AccountMenu-title">
                          {userContext.userSession.firstname && userContext.userSession.lastname
                            ? `${userContext.userSession.firstname} ${userContext.userSession.lastname}`
                            : `${userContext.userSession.email}`}
                        </div>
                      )}
                    </div>
                    <div className="AccountMenu-content">
                      <ul className="AccountMenu-list">
                        <li className="AccountMenu-item">
                          <Link href="/profile/user">
                            <a className="AccountMenu-link">
                              <Svg
                                modifiers={['AccountMenu-linkIcon', 'AccountMenu-linkIcon--profile']}
                                id="icon-profile"
                              />
                              <span className="AccountMenu-linkLabel">{t('ACCOUNTMENU__ACCOUNTINFO')}</span>
                              <Svg modifiers={['AccountMenu-linkChevron']} id="icon-chevron-right" />
                            </a>
                          </Link>
                        </li>
                        <li className="AccountMenu-item">
                          <Link href="/profile/team">
                            <a className="AccountMenu-link">
                              <Svg
                                modifiers={['AccountMenu-linkIcon', 'AccountMenu-linkIcon--settings']}
                                id="icon-settings"
                              />
                              <span className="AccountMenu-linkLabel">{t('ACCOUNTMENU__BTN_SETTINGS')}</span>
                              <Svg modifiers={['AccountMenu-linkChevron']} id="icon-chevron-right" />
                            </a>
                          </Link>
                        </li>
                        <li className="AccountMenu-item">
                          {/* eslint-disable @next/next/no-html-link-for-pages */}
                          <a className="AccountMenu-link" href="/api/rinus/logout">
                            <Svg
                              modifiers={['AccountMenu-linkIcon', 'AccountMenu-linkIcon--logout']}
                              id="icon-logout"
                            />
                            <span className="AccountMenu-linkLabel">{t('ACCOUNTMENU__BTN_LOGOUT')}</span>
                            <Svg modifiers={['AccountMenu-linkChevron']} id="icon-chevron-right" />
                          </a>
                          {/* eslint-enable @next/next/no-html-link-for-pages */}
                        </li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="AccountMenu-section">
                      <div className="AccountMenu-heading">
                        {userContext.userSession && (
                          <div className="AccountMenu-title">
                            {userContext.userSession.firstname && userContext.userSession.lastname
                              ? `${userContext.userSession.firstname} ${userContext.userSession.lastname}`
                              : `${userContext.userSession.email}`}
                          </div>
                        )}
                      </div>
                      <div className="AccountMenu-content">
                        <ul className="AccountMenu-list">
                          {process.env.NEXT_PUBLIC_SETTINGS_URL && (
                            <li className="AccountMenu-item">
                              <a
                                className="AccountMenu-link"
                                href={process.env.NEXT_PUBLIC_SETTINGS_URL}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Svg
                                  modifiers={['AccountMenu-linkIcon', 'AccountMenu-linkIcon--profile']}
                                  id="icon-profile"
                                />
                                <span className="AccountMenu-linkLabel">{t('ACCOUNTMENU__ACCOUNTINFO')}</span>
                                <Svg modifiers={['AccountMenu-linkChevron']} id="icon-chevron-right" />
                              </a>
                            </li>
                          )}
                          <li className="AccountMenu-item">
                            {/* eslint-disable @next/next/no-html-link-for-pages */}
                            <a className="AccountMenu-link" href="/api/rinus/logout">
                              <Svg
                                modifiers={['AccountMenu-linkIcon', 'AccountMenu-linkIcon--logout']}
                                id="icon-logout"
                              />
                              <span className="AccountMenu-linkLabel">{t('ACCOUNTMENU__BTN_LOGOUT')}</span>
                              <Svg modifiers={['AccountMenu-linkChevron']} id="icon-chevron-right" />
                            </a>
                            {/* eslint-enable @next/next/no-html-link-for-pages */}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="AccountMenu-section">
                      <div className="AccountMenu-heading">
                        <div className="AccountMenu-title">{t('ACCOUNTMENU__PLATFORM_TITLE')}</div>
                      </div>
                      <div className="AccountMenu-content">
                        <ul className="AccountMenu-list">
                          <li className="AccountMenu-item">
                            <Link href="/profile/user">
                              <a className="AccountMenu-link">
                                <Svg
                                  modifiers={['AccountMenu-linkIcon', 'AccountMenu-linkIcon--settings']}
                                  id="icon-settings"
                                />
                                <span className="AccountMenu-linkLabel">{t('ACCOUNTMENU__BTN_SETTINGS')}</span>
                                <Svg modifiers={['AccountMenu-linkChevron']} id="icon-chevron-right" />
                              </a>
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="AccountMenu-section">
                <div className="AccountMenu-content">
                  {process.env.NEXT_PUBLIC_THEME !== 'sportwide' && (
                    <Svg modifiers={['AccountMenu-logo']} id={`logo-${theme}account`} />
                  )}
                  <p
                    className={`AccountMenu-description ${
                      process.env.NEXT_PUBLIC_THEME !== 'sportwide' ? '' : 'AccountMenu-descriptionMargin'
                    }`}
                  >
                    {t('ACCOUNTMENU__DESCRIPTION')}
                  </p>
                  <div className="AccountMenu-buttonGroup">
                    <Button
                      label={t('ACCOUNTMENU__BTN_LOGIN')}
                      href={'/api/rinus/login'}
                      modifiers={['Button--account', 'Button--accountLogin']}
                      icon={{ id: 'icon-chevron-right' }}
                      image={
                        process.env.NEXT_PUBLIC_THEME !== 'sportwide'
                          ? { svgId: `logo-${process.env.NEXT_PUBLIC_THEME}nl` }
                          : null
                      }
                    />
                    <Button
                      label={t('ACCOUNTMENU__BTN_SIGNUP')}
                      href={'/api/rinus/register'}
                      modifiers={['Button--account', 'Button--accountRegister']}
                      icon={{ id: 'icon-chevron-right' }}
                      image={
                        process.env.NEXT_PUBLIC_THEME !== 'sportwide'
                          ? { svgId: `logo-${process.env.NEXT_PUBLIC_THEME}nl` }
                          : null
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="AccountMenu-overlay" onClick={() => setToggleState(!toggleState)} />
      </div>
    </div>
  )
}

export default AccountMenu
